/**
 * Booking System Types
 * Types for Calendly-like booking functionality
 */

export interface TimeSlot {
  time: string; // HH:mm format
  available: boolean;
}

export interface AvailabilitySlot {
  dayOfWeek: number; // 0 = Sunday, 6 = Saturday
  startTime: string; // HH:mm
  endTime: string; // HH:mm
}

export interface UserAvailability {
  userId: string;
  slots: AvailabilitySlot[];
  timezone: string;
  slotDuration: number; // minutes
  bufferBetweenMeetings: number; // minutes
}

export interface BookingRequest {
  hostId: string;
  guestName: string;
  guestEmail: string;
  guestPhone?: string;
  date: string; // ISO date
  time: string; // HH:mm
  duration: number; // minutes
  title: string;
  description?: string;
  dealId?: string;
}

export interface BookingConfirmation {
  id: string;
  meetingId: string;
  meetingUrl: string;
  date: string;
  time: string;
  duration: number;
  title: string;
  hostName: string;
  guestName: string;
  guestEmail: string;
}

export const DEFAULT_AVAILABILITY: AvailabilitySlot[] = [
  { dayOfWeek: 1, startTime: '09:00', endTime: '17:00' }, // Monday
  { dayOfWeek: 2, startTime: '09:00', endTime: '17:00' }, // Tuesday
  { dayOfWeek: 3, startTime: '09:00', endTime: '17:00' }, // Wednesday
  { dayOfWeek: 4, startTime: '09:00', endTime: '17:00' }, // Thursday
  { dayOfWeek: 5, startTime: '09:00', endTime: '17:00' }, // Friday
];

export const BOOKING_TRANSLATIONS = {
  en: {
    title: 'Book a Meeting',
    selectDate: 'Select a date',
    selectTime: 'Select a time',
    yourDetails: 'Your details',
    name: 'Your name',
    email: 'Email address',
    phone: 'Phone (optional)',
    notes: 'Additional notes',
    bookMeeting: 'Book Meeting',
    booking: 'Booking...',
    confirmTitle: 'Meeting Confirmed!',
    confirmMessage: 'Your meeting has been scheduled.',
    meetingWith: 'Meeting with',
    dateTime: 'Date & Time',
    duration: 'Duration',
    minutes: 'minutes',
    joinLink: 'Join Link',
    copyLink: 'Copy Link',
    linkCopied: 'Link copied!',
    noSlots: 'No available slots for this date',
    selectDateFirst: 'Please select a date first',
    errorBooking: 'Failed to book meeting. Please try again.',
    availability: 'Availability',
    setAvailability: 'Set your availability',
    slotDuration: 'Meeting duration',
    bufferTime: 'Buffer between meetings',
    saveAvailability: 'Save Availability',
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
    from: 'From',
    to: 'To',
  },
  fr: {
    title: 'Réserver un rendez-vous',
    selectDate: 'Sélectionnez une date',
    selectTime: 'Sélectionnez une heure',
    yourDetails: 'Vos informations',
    name: 'Votre nom',
    email: 'Adresse email',
    phone: 'Téléphone (optionnel)',
    notes: 'Notes additionnelles',
    bookMeeting: 'Réserver',
    booking: 'Réservation...',
    confirmTitle: 'Rendez-vous confirmé!',
    confirmMessage: 'Votre rendez-vous a été programmé.',
    meetingWith: 'Rendez-vous avec',
    dateTime: 'Date & Heure',
    duration: 'Durée',
    minutes: 'minutes',
    joinLink: 'Lien de la réunion',
    copyLink: 'Copier le lien',
    linkCopied: 'Lien copié!',
    noSlots: 'Aucun créneau disponible pour cette date',
    selectDateFirst: 'Veuillez d\'abord sélectionner une date',
    errorBooking: 'Échec de la réservation. Veuillez réessayer.',
    availability: 'Disponibilités',
    setAvailability: 'Définir vos disponibilités',
    slotDuration: 'Durée des réunions',
    bufferTime: 'Temps entre les réunions',
    saveAvailability: 'Enregistrer',
    monday: 'Lundi',
    tuesday: 'Mardi',
    wednesday: 'Mercredi',
    thursday: 'Jeudi',
    friday: 'Vendredi',
    saturday: 'Samedi',
    sunday: 'Dimanche',
    from: 'De',
    to: 'À',
  },
};
